import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { AnimatePresence } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import CrossIcon from '@common/assets/icons/cross.svg?react';
import HeartIcon from '@common/assets/icons/heart.svg?react';
import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { IconButton } from '@common/ui/buttons/IconButton';
import { Disclaimer } from '@common/ui/Disclaimer';
import { Loader } from '@common/ui/Loader';
import { useEventEmit } from '@common/utils/events';
import { getApiErrorInfo, isRateLimitedError } from '@common/utils/fetcher';
import { finishInvite, readPendingInvite } from '@common/utils/startParam';

import { useAccountStore } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import {
  SUPERLIKE_HELD_NOTICE,
  useSuperlikeSend,
} from '@entities/dating/hooks/useSuperlikeSend';
import { MatchDialog } from '@entities/dating/ui/MatchDialog';
import { ProfileCard } from '@entities/dating/ui/ProfileCard';
import { ProfileInfo } from '@entities/dating/ui/ProfileInfo';
import { useInviteIntroStore } from '@entities/matchmaker';
import {
  isIncomingSuperlikeCard,
  SuperlikeButton,
  SuperlikeLimitSheet,
  SuperlikeSendAnimation,
  SuperlikeSheet,
  usePrefersReducedMotion,
  useSuperlikeSendAnimation,
} from '@entities/superlike/ui';

import { ACCOUNT_ERROR_CODES } from '@services/AccountService';
import { analytics } from '@services/AnalyticsService';
import { usePostSwipe } from '@services/hooks/MatchesService';
import { usePostMatchmakerInviteCandidate } from '@services/hooks/MatchmakerService';
import {
  MATCHMAKER_ERROR_CODES,
  type PostMatchmakerInviteCandidateResponse,
} from '@services/MatchmakerService';
import { type SuperlikeSendStatus } from '@services/SuperlikesService';
import { type User } from '@services/UsersService';

import { MatchmakerResult } from '@widgets/matchmaker/components/MatchmakerResult';

import styles from './InviteCandidate.module.scss';

type LandingView =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'available'; candidate: User }
  | { kind: 'already_swiped' }
  | { kind: 'unavailable' };

/** The strip's 48 px and its 8 px padding above and below. */
const STRIP_HEIGHT = 64;
// The superlike heart takes 68 px beside the card's text (UI2).
const SUPERLIKE_CONTENT_INSET = 68;

const RATE_LIMITED_NOTICE = {
  title: 'Слишком много действий',
  description: 'Попробуйте ещё раз чуть позже',
};

const swipeLimitNotice = () => {
  const resetTime = useAuthStore.getState().swipe_status?.reset_time;

  return {
    title: 'Лимит свайпов исчерпан',
    description: `До обновления свайпов осталось ${moment(resetTime).fromNow(true)}`,
  };
};

/** One swipe from a non-Premium count, as an answer takes it. */
const takeSwipe = () => {
  const { swipe_status: swipeStatus, setSwipeStatus } = useAuthStore.getState();

  if (typeof swipeStatus?.remaining === 'number') {
    setSwipeStatus({
      ...swipeStatus,
      remaining: Math.max(0, swipeStatus.remaining - 1),
    });
  }
};

/**
 * `/invite/candidate` (plan §4, board 4.3): the recommended card, answered
 * like a feed card. Any final state finishes the invite, so a reload never
 * lands here again.
 */
export const InviteCandidate = () => {
  const navigate = useNavigate();
  const emit = useEventEmit();
  const tabbar = useTabbar();
  // The slot's setter, without making it an effect dependency.
  const tabbarRef = useRef(tabbar);
  tabbarRef.current = tabbar;
  const intro = useInviteIntroStore((state) => state.intro);
  const isDating = useAccountStore((state) => state.account?.dating !== false);
  const approvalStatus = useAuthStore(
    (state) => state.user?.approval_status?.status,
  );
  const isApproved = approvalStatus === 'approved';

  const [invite] = useState(() => readPendingInvite());
  const [view, setView] = useState<LandingView>(
    invite ? { kind: 'loading' } : { kind: 'unavailable' },
  );
  const [isDatingOff, setIsDatingOff] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [cardKey, setCardKey] = useState(0);
  const [matchProfile, setMatchProfile] = useState<User>();
  // The match dialog's first text: empty after a superlike's match.
  const [matchInitialMessage, setMatchInitialMessage] = useState<string>();
  const isLoadedRef = useRef(false);
  const isMountedRef = useRef(true);

  const { trigger: triggerPostMatchmakerInviteCandidate } =
    usePostMatchmakerInviteCandidate();
  const { trigger: triggerPostSwipe } = usePostSwipe();

  const canAnswer = isDating && !isDatingOff;
  const strip = intro && invite && intro.token === invite.token ? intro : null;

  const handleLanding = (
    token: string,
    response: PostMatchmakerInviteCandidateResponse,
  ) => {
    analytics.trackMmInviteLanding(response.state);

    if (response.state === 'available') {
      setView({ kind: 'available', candidate: response.candidate });

      return;
    }

    finishInvite(token);

    if (response.state === 'matched') {
      navigate({
        to: '/chats/$matchId',
        params: { matchId: response.match_id },
        replace: true,
      });

      return;
    }

    // A state this client does not know (a newer server) reads as unavailable.
    setView({
      kind:
        response.state === 'already_swiped' ? 'already_swiped' : 'unavailable',
    });
  };

  const loadCandidate = async () => {
    if (!invite?.opened) return;

    setView({ kind: 'loading' });

    try {
      const response = await triggerPostMatchmakerInviteCandidate({
        token: invite.token,
      });

      handleLanding(invite.token, response);
    } catch (error) {
      const { status, code } = getApiErrorInfo(error);

      // The inviter's matchmaker feature is off: the invite is gone for good.
      if (status === 404 && code === MATCHMAKER_ERROR_CODES.featureDisabled) {
        analytics.trackMmInviteLanding('feature_disabled');
        finishInvite(invite.token);
        setView({ kind: 'unavailable' });

        return;
      }

      setView({ kind: 'failed' });
    }
  };

  useEffect(() => {
    if (!invite) return;

    if (!invite.opened) {
      navigate({ to: '/invite', replace: true });

      return;
    }

    if (isLoadedRef.current) return;

    isLoadedRef.current = true;
    void loadCandidate();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The superlike heart (plan §4: the landing's card answers like a feed
  // card). Errors are the feed's, by code, and never finish the invite.
  const isReducedMotion = usePrefersReducedMotion();
  const superlikeAnimation = useSuperlikeSendAnimation();
  const [isSuperlikeSent, setIsSuperlikeSent] = useState(false);

  // 200: the superlike is the answer. The invite is finished at once (a
  // reload must not land here again); the animation plays, then `held` says
  // so and the user goes on to the feed, or a match opens its dialog.
  const handleSuperlikeSent = (
    candidate: User,
    status: SuperlikeSendStatus,
  ) => {
    if (invite) finishInvite(invite.token);

    // The answer came after the screen was left: only the swipe counts.
    if (!isMountedRef.current) {
      takeSwipe();

      return;
    }

    setIsSuperlikeSent(true);
    superlikeAnimation.play({
      isReducedMotion,
      isEndingAfterBurst: status === 'matched',
      onDone: () => {
        takeSwipe();

        if (!isMountedRef.current) return;

        if (status === 'matched') {
          setMatchInitialMessage('');
          setMatchProfile(candidate);

          return;
        }

        if (status === 'held') emit('info', SUPERLIKE_HELD_NOTICE);

        navigate({ to: '/dating', replace: true });
      },
    });
  };

  const superlike = useSuperlikeSend({
    isBlocked: () => superlikeAnimation.isPlayingRef.current,
    onSent: (candidate, { status }) => handleSuperlikeSent(candidate, status),
    onSwipeLimit: () => emit('error', swipeLimitNotice()),
    onRateLimited: () => emit('info', RATE_LIMITED_NOTICE),
    onNotDating: () => setIsDatingOff(true),
  });

  const handleAnswer = async (action: 'like' | 'pass', candidate: User) => {
    if (!invite || isSending || superlikeAnimation.isPlayingRef.current) return;

    setIsSending(true);

    try {
      await triggerPostSwipe({ action, target_user_id: candidate.id });

      takeSwipe();
      finishInvite(invite.token);

      if (action === 'like' && candidate.liked_me && isApproved) {
        setMatchInitialMessage(undefined);
        setMatchProfile(candidate);

        return;
      }

      navigate({ to: '/dating', replace: true });
    } catch (error) {
      setCardKey((key) => key + 1);

      if (isRateLimitedError(error)) {
        emit('info', RATE_LIMITED_NOTICE);

        return;
      }

      const { status, code } = getApiErrorInfo(error);

      if (code === ACCOUNT_ERROR_CODES.notDating) {
        setIsDatingOff(true);

        return;
      }

      if (status === 429) {
        emit('error', swipeLimitNotice());

        return;
      }

      emit('error', {
        title: 'Ошибка действия',
        description: 'Попробуйте позже',
      });
    } finally {
      setIsSending(false);
    }
  };

  const candidate = view.kind === 'available' ? view.candidate : undefined;
  // The heart: with the feature, for an invitee who can answer, and not on
  // a candidate who already liked or superliked the invitee.
  const hasSuperlikeHeart =
    !!candidate &&
    superlike.isEnabled &&
    canAnswer &&
    !candidate.liked_me &&
    !isIncomingSuperlikeCard(candidate);
  // The slot stays empty while a superlike sheet is open (its buttons are in
  // the sheet) and once one is sent.
  const isSuperlikeBusy = superlike.isSheetOpen || isSuperlikeSent;

  useEffect(() => {
    if (!candidate || matchProfile || isSuperlikeBusy) {
      tabbarRef.current.setBottomSheet(undefined);

      return;
    }

    if (!canAnswer) {
      tabbarRef.current.setBottomSheet(
        <BottomSheet
          title="Чтобы ответить, включите знакомства"
          buttons={[
            <Button
              testId="invite-candidate-enable-dating-button"
              variant="actionPrimary"
              onClick={() => navigate({ to: '/profile/settings' })}
            >
              Включить знакомства
            </Button>,
          ]}
        />,
      );

      return;
    }

    tabbarRef.current.setBottomSheet(
      <BottomSheet
        buttonDirection="row"
        buttons={[
          <IconButton
            testId="invite-candidate-pass-button"
            size={500}
            variant="negativeSecondary"
            disabled={isSending}
            onClick={() => void handleAnswer('pass', candidate)}
          >
            <CrossIcon />
          </IconButton>,
          <IconButton
            testId="invite-candidate-like-button"
            size={500}
            variant="actionSecondary"
            disabled={isSending}
            onClick={() => void handleAnswer('like', candidate)}
          >
            <HeartIcon />
          </IconButton>,
        ]}
      />,
    );
  }, [candidate, canAnswer, isSending, isSuperlikeBusy, matchProfile]);

  useEffect(() => () => tabbarRef.current.setBottomSheet(undefined), []);

  const renderFinal = (testId: string, title: string) => (
    <MatchmakerResult
      testId={testId}
      icon3d="not-found.png"
      title={title}
      button={
        <Button
          testId="invite-candidate-continue-button"
          onClick={() => navigate({ to: '/dating', replace: true })}
        >
          Продолжить
        </Button>
      }
    />
  );

  const renderView = () => {
    switch (view.kind) {
      case 'loading':
        return <Loader />;
      case 'failed':
        return (
          <MatchmakerResult
            testId="invite-candidate-failed"
            icon3d="sad.png"
            title="Ошибка подключения"
            description="Не удалось подключиться к серверу"
            button={
              <Button
                testId="invite-candidate-retry-button"
                onClick={() => void loadCandidate()}
              >
                Повторить
              </Button>
            }
          />
        );
      case 'already_swiped':
        return renderFinal(
          'invite-candidate-already-swiped',
          'Вы уже ответили на эту анкету',
        );
      case 'unavailable':
        return renderFinal(
          'invite-candidate-unavailable',
          'Эта анкета сейчас недоступна',
        );
      case 'available':
        return (
          <>
            <div className={styles.stripRow}>
              <div
                className={styles.strip}
                data-test-id="invite-candidate-strip"
              >
                <span className={styles.stripText}>
                  <span className={styles.stripName}>
                    {strip?.matchmakerName ? (
                      <>
                        <span className="ym-hide-content">
                          {strip.matchmakerName}
                        </span>{' '}
                        советует
                      </>
                    ) : (
                      'Тебе советуют эту анкету'
                    )}
                  </span>
                  {strip?.note && (
                    <span
                      className={`${styles.stripNote} ym-hide-content`}
                      data-test-id="invite-candidate-strip-note"
                    >
                      {' '}
                      · «{strip.note}»
                    </span>
                  )}
                </span>
              </div>
            </div>

            <div
              className={styles.card}
              style={{
                height: `calc(100vh - var(--tabbar-height) - ${STRIP_HEIGHT}px - 8px)`,
              }}
            >
              <ProfileCard
                key={cardKey}
                variant="dating"
                profile={view.candidate}
                canSwipes={canAnswer && !isSending && !isSuperlikeSent}
                onLike={(profile) => void handleAnswer('like', profile)}
                onSkip={(profile) => void handleAnswer('pass', profile)}
                contentInsetRight={
                  hasSuperlikeHeart ? SUPERLIKE_CONTENT_INSET : undefined
                }
                overlay={
                  hasSuperlikeHeart ? (
                    <>
                      <SuperlikeButton
                        remaining={superlike.remaining}
                        canBeat={!isSuperlikeBusy}
                        isPressed={
                          superlike.target !== null ||
                          superlikeAnimation.phase === 'launch0'
                        }
                        onClick={() => {
                          if (!isSending) superlike.open(view.candidate);
                        }}
                      />
                      {isSuperlikeSent && (
                        <SuperlikeSendAnimation
                          phase={superlikeAnimation.phase}
                        />
                      )}
                    </>
                  ) : undefined
                }
                superlikeSend={
                  isSuperlikeSent
                    ? { phase: superlikeAnimation.phase, role: 'sent' }
                    : undefined
                }
              />

              {canAnswer && !isApproved && (
                <div
                  className={styles.notice}
                  data-test-id="invite-candidate-pending-notice"
                >
                  <Disclaimer
                    title="Ваша анкета на проверке"
                    body="Лайк дойдёт сразу после неё"
                    type="info"
                    closable={false}
                    style={{
                      background: 'var(--graphic-primary-on-dark-transparent)',
                    }}
                  />
                </div>
              )}
            </div>

            <ProfileInfo profile={view.candidate} />
          </>
        );
    }
  };

  return (
    <div className={styles.inviteCandidate} data-test-id="invite-candidate">
      {renderView()}

      <AnimatePresence>
        {matchProfile && (
          <MatchDialog
            matchProfile={matchProfile}
            initialMessage={matchInitialMessage}
            onClose={() => navigate({ to: '/dating', replace: true })}
          />
        )}
      </AnimatePresence>

      {superlike.target && (
        <SuperlikeSheet
          name={superlike.target.username}
          remaining={superlike.remaining}
          message={superlike.message}
          messageError={superlike.messageError}
          isSending={superlike.isSending}
          onMessageChange={superlike.changeMessage}
          onSend={() => void superlike.send()}
          onClose={superlike.close}
        />
      )}

      {superlike.limitSheet && (
        <SuperlikeLimitSheet
          resetTime={superlike.limitSheet.resetTime}
          isPremium={superlike.isPremium}
          onPremium={superlike.openPremium}
          onClose={superlike.closeLimitSheet}
        />
      )}
    </div>
  );
};
